import { BUILD_ID } from '../version';

// App-update detection (operator 2026-08-07). One tiny module-level store so
// the two ways a stale tab learns about a new deploy land in the SAME state:
//
//  - PROACTIVE: UpdateToast polls /version.json and compares the served id
//    against the embedded BUILD_ID → status 'available' (refresh optional).
//  - REACTIVE: a stale tab lazy-loads a chunk the new deploy no longer serves;
//    the ErrorBoundary detects the ChunkLoadError → status 'required' (the tab
//    is already broken, refresh is the only way out).
//
// Module-level (not React context) on purpose: the ErrorBoundary must be able
// to report into it while the tree inside it is crashing, and it must survive
// Layout remounts (Layout remounts on every route change).

let state = { status: 'idle', latestId: null };
const listeners = new Set();

export function getUpdateState() {
  return state;
}

export function subscribeUpdateState(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setState(next) {
  state = next;
  listeners.forEach((listener) => listener(state));
}

// 'required' is sticky — a broken tab can't be talked back down to optional.
export function notifyUpdateRequired() {
  if (state.status !== 'required') setState({ ...state, status: 'required' });
}

// Chunk-load failures are how a stale tab actually breaks: the new deploy
// stopped serving the content-hashed chunk this bundle still references.
// Covers webpack's ChunkLoadError (js + css) and the native dynamic-import
// wording some browsers throw instead.
export function isChunkLoadError(error) {
  if (!error) return false;
  const message = String(error.message || '');
  return (
    error.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /(Failed to fetch|error loading) dynamically imported module/i.test(message)
  );
}

// One poll tick. Fetch failures are swallowed — offline / a deploy mid-swap is
// transient and the next tick retries; a version check must never surface an
// error of its own.
export async function checkForUpdate() {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) return;
    const data = await response.json();
    const id = data && data.id;
    if (!id || state.status === 'required') return;
    if (id === BUILD_ID) {
      // Rolled back to the version this tab runs — stop nagging.
      if (state.status === 'available') setState({ status: 'idle', latestId: null });
      return;
    }
    if (state.status !== 'available' || state.latestId !== id) {
      // latestId moving forward is what lets a dismissed toast re-appear on the
      // NEXT bump (UpdateToast compares it to the id the user dismissed).
      setState({ status: 'available', latestId: id });
    }
  } catch (error) {
    /* transient — retry on the next tick */
  }
}

// Test-only: the store is module-level, so parallel tests need a reset.
export function resetUpdateStateForTests() {
  state = { status: 'idle', latestId: null };
}
